// Central registry for monetization / partner links. Keeping these in one
// place means affiliate tags, referral programs, and disclosure copy live
// together and can be swapped without hunting through components.
//
// MODEL (2026-08, revised): educate → graduate → lead. Everything the reader
// touches is FREE, myths, Survival Kit, Deal-Checker, the EV match, and the
// deal review. No paid services, no "we run your deal" advisory (the owner is
// employed full-time and isn't running deals).
//
// 2026-08-09 CORRECTION: the site used to promise a DEALER INTRODUCTION as
// the graduation step. There are no dealer relationships, so that promise
// could not be kept; in ~7 weeks live it was also never once taken up (0 real
// `concierge` leads against 67 /concierge views, while the free-PDF and
// "check my rebates" asks converted). The graduation step is now a FREE
// PERSONAL DEAL REVIEW, a real person reads the quote and says what's wrong
// with it, which is deliverable solo, today, and collects the vehicle/ZIP/
// timing detail that a lead actually needs to be worth something.
//
// Say nothing about dealers paying us until a dealer actually does. Passive
// side income: charger affiliate links (Amazon) and partner referrals (Qmerit
// URL still a placeholder, not a live referral link).

export const AMAZON_TAG = 'adamapps-20';

// Build an Amazon affiliate URL from a search term or product URL.
export function amazonSearch(term) {
  return `https://www.amazon.com/s?k=${encodeURIComponent(term)}&tag=${AMAZON_TAG}`;
}

export const PARTNERS = {
  // Home Level 2 charger install referral. Qmerit runs a formal referral
  // program; swap in the real referral URL once enrolled.
  chargerInstall: {
    name: 'Qmerit',
    label: 'Get a home charger installed (vetted electricians)',
    url: 'https://qmerit.com/',           // TODO: replace with referral link once enrolled
    blurb: 'Certified installers, upfront quotes, handles permits.',
    enabled: true,
  },
  // Insurance quote lead-gen ($25–$50+/valid quote). Placeholder until a
  // network/partner is chosen.
  insurance: {
    name: 'EV insurance quotes',
    label: 'Compare EV insurance in 2 minutes',
    url: '',                               // TODO: affiliate/network link
    blurb: "EVs can be cheaper to insure than people think, or pricier. Know before you sign.",
    enabled: false,
  },
  // Charger hardware (already monetized via Amazon).
  chargerHardware: {
    name: 'Level 2 chargers',
    label: 'See recommended home chargers',
    url: '/chargers',
    blurb: 'Our picks for Level 2 home charging.',
    enabled: true,
  },
};

// What we offer. All free for the reader: the education builds the audience,
// and ready buyers graduate to a free deal review (the lead is the business,
// but nobody is paying for it yet, so we don't claim they are). NOTE:
// leadType values are established strings the live backend already receives,
// don't invent new ones without checking the backend first. The review tier
// deliberately keeps leadType 'concierge' for that reason.
export const SERVICE_TIERS = [
  {
    id: 'survival_kit',
    name: 'The Dealership Survival Kit',
    price: 'Free',
    tagline: "Walk in knowing more about the EV than the salesperson.",
    blurb:
      "The printable cheat sheet: every junk fee to decline, what to actually pay, and the questions that expose a dealer who's never sold an EV. Costs you nothing.",
    cta: 'Get the free kit',
    leadType: 'survival_kit',
    highlight: false,
  },
  {
    id: 'deal_review',
    name: 'The Deal Review',
    price: 'Free',
    tagline: 'Got a quote? A real person tells you what\'s wrong with it.',
    blurb:
      "Send us the out-the-door sheet, or just the car you're circling, and we read it ourselves: the fees to refuse, the number you should actually be paying, and the line to say to get there. No dealer is paying for your name. We just want you to stop losing this fight.",
    cta: 'Have my deal reviewed',
    leadType: 'concierge',
    highlight: true,
  },
  {
    id: 'price_audit',
    name: 'The Deal-Checker',
    price: 'Free',
    tagline: 'Already holding a quote? Run it through the checker.',
    blurb:
      "Paste your out-the-door sheet into the free Deal-Checker and it flags every bogus add-on, fake fee, and 'market adjustment' instantly, in your browser, nothing stored. Then walk back in knowing exactly what to refuse.",
    cta: 'Check my quote',
    leadType: 'price_audit',
    highlight: false,
  },
];

// The dealer promise, said up front wherever the buying flow asks for anything
// (Nov 2026: a friend of the owner read "Get help buying" as a dealer lead form,
// because the promise only showed after the 7 questions). One source, so if a
// dealer partnership ever starts, this changes first, everywhere at once.
export const DEALER_PROMISE = {
  headline: "We don't sell your info to dealers.",
  body: "We don't work with any dealers, and no dealer pays us. Nothing you tell us goes to one. If that ever changes, we'll say so right here first, and we'll ask you before anything you've sent us is shared.",
  short: "We don't sell or share your info, and no dealer ever gets it.",
};

// Transparency copy shown on the How-It-Works page. States the revenue model
// honestly. Rewritten 2026-08-09: the old version said dealers pay us for
// introductions. No dealer has ever paid us anything, so it now says that.
export const MONEY_DISCLOSURE =
  "Real talk on money, no BS: the fact-checks, the Dealership Survival Kit, the Deal-Checker and the deal review are free, and they're staying free. Here's the part most sites won't print: right now nobody pays us for any of it. No dealer is buying your name, because we haven't got dealers. If that changes, this paragraph changes with it, in plain English, before you hand us anything. Two standing promises: (1) we tell you exactly when money changes hands, and (2) we don't sell your email, not to a dealer, not to a list, not to ten cold-callers. The one thing we do make a little on: if you buy a home charger or get one installed through a link here, we may earn a small commission at no extra cost to you. Our whole job is keeping you from getting fleeced. Everything we build has to pass that test first.";
